import type { ReactNode } from "react";

/**
 * Window control glyphs on a 14×14 canvas that fills the whole button
 * (rendered `absolute inset-0`), drawn symmetric about the centre (7,7).
 *
 * Geometry is measured from native macOS traffic lights (14pt buttons at
 * 2x), so ink extents rather than path coordinates are what must match:
 * - close: 7×7 ink (3.5 → 10.5), 1.5 stroke, round caps
 * - minimize: 7.6 wide (3.2 → 10.8), 1.8 thick, round caps
 * - full screen: two filled right triangles in a 6×6 box (4 → 10), 5pt legs,
 *   ~1.4pt diagonal gap, softly rounded corners; restore uses the same
 *   triangles with their right angles meeting at the centre
 * Round caps/joins extend ink by half the stroke, so path points are inset
 * by that amount.
 */

interface ControlIconProps {
  className?: string;
}

function ControlSvg({
  className,
  children,
}: ControlIconProps & { children: ReactNode }) {
  return (
    <svg
      viewBox="0 0 14 14"
      fill="none"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className={className}
    >
      {children}
    </svg>
  );
}

const CARET_ROUNDING = 1;

function Caret({ points }: { points: string }) {
  return (
    <polygon
      points={points}
      fill="currentColor"
      strokeWidth={CARET_ROUNDING}
    />
  );
}

export function WindowMinusIcon({ className }: ControlIconProps) {
  return (
    <ControlSvg className={className}>
      <line x1="4.1" y1="7" x2="9.9" y2="7" strokeWidth={1.8} />
    </ControlSvg>
  );
}

/** Enter full screen — triangles out toward the top-left and bottom-right. */
export function WindowPlusIcon({ className }: ControlIconProps) {
  return (
    <ControlSvg className={className}>
      <Caret points="4.5,4.5 7.8,4.5 4.5,7.8" />
      <Caret points="9.5,9.5 6.2,9.5 9.5,6.2" />
    </ControlSvg>
  );
}

export function WindowCloseIcon({ className }: ControlIconProps) {
  return (
    <ControlSvg className={className}>
      <g strokeWidth={1.5}>
        <line x1="4.25" y1="4.25" x2="9.75" y2="9.75" />
        <line x1="9.75" y1="4.25" x2="4.25" y2="9.75" />
      </g>
    </ControlSvg>
  );
}

/**
 * Leave full screen — matches the system ExitFullScreen artwork: the same
 * triangles, right-angle corners meeting at the centre, hypotenuses outward.
 */
export function WindowRestoreIcon({ className }: ControlIconProps) {
  return (
    <ControlSvg className={className}>
      <Caret points="6.5,6.5 3.2,6.5 6.5,3.2" />
      <Caret points="7.5,7.5 10.8,7.5 7.5,10.8" />
    </ControlSvg>
  );
}
